import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { usePrinting } from '../PrintingContext';
import { normalizeRole } from '../utils/userLocale';
import { printServiceLabel } from '../utils/printServiceLabels';
import { downloadCsv, exportTableToPdf } from '../utils/reportExport';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from '../utils/submitFeedback';
import './PrintingPage.css';

function formatJobDate(value) {
  if (value == null || value === '') return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(value);
  }
}

function statusVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'partial') return 'partial';
  return 'unpaid';
}

const PRINT_EXPORT_HEAD_CSV = [
  'id',
  'pages',
  'service',
  'rate_tzs',
  'total_tzs',
  'paid_tzs',
  'balance_tzs',
  'status',
  'created_by',
  'created_at',
];

const PRINT_EXPORT_HEAD_PDF = [
  'ID',
  'Pages',
  'Service',
  'Rate (TZS)',
  'Total (TZS)',
  'Paid (TZS)',
  'Balance (TZS)',
  'Status',
  'By',
  'Date',
];

function buildPrintJobExportRows(jobs, serviceLabel) {
  return jobs.map((job) => [
    job.id != null ? String(job.id) : '',
    job.pages != null ? String(job.pages) : '',
    serviceLabel(job.printType),
    job.rate != null ? String(job.rate) : '',
    job.totalAmount != null ? String(job.totalAmount) : '',
    job.paidAmount != null ? String(job.paidAmount) : '',
    job.balance != null ? String(job.balance) : '',
    job.status != null ? String(job.status) : '',
    job.createdBy != null ? String(job.createdBy) : '',
    job.createdAt != null ? String(job.createdAt) : '',
  ]);
}

const PrintingPage = ({ mode = 'hub', onNavigate }) => {
  const { user: currentUser } = useAuth();
  const {
    printJobs,
    addPrintJob,
    recordPayment,
    savePrintLabSettings,
    stats,
    RATE_BW,
    RATE_COLOR,
    RATE_COPY,
    RATE_SCAN,
    RATE_CUSTOMER_REAM,
    refreshPrintJobs,
  } = usePrinting();
  const [pages, setPages] = useState('');
  const [printType, setPrintType] = useState('bw');
  const [paymentJob, setPaymentJob] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [jobPhase, setJobPhase] = useState('idle');
  const [payPhase, setPayPhase] = useState('idle');
  const [ratesPhase, setRatesPhase] = useState('idle');
  const [historyPdfBusy, setHistoryPdfBusy] = useState(false);
  const [historyExportError, setHistoryExportError] = useState('');
  const [ownerBw, setOwnerBw] = useState(String(RATE_BW));
  const [ownerColor, setOwnerColor] = useState(String(RATE_COLOR));
  const [ownerCopy, setOwnerCopy] = useState(String(RATE_COPY));
  const [ownerScan, setOwnerScan] = useState(String(RATE_SCAN));
  const [ownerCustomerReam, setOwnerCustomerReam] = useState(String(RATE_CUSTOMER_REAM));
  const redirectJobRef = useRef(null);
  const redirectPayRef = useRef(null);
  const historyPdfInFlight = useRef(false);
  const isOwner = normalizeRole(currentUser?.role) === 'owner';

  useEffect(() => {
    setOwnerBw(String(RATE_BW));
    setOwnerColor(String(RATE_COLOR));
    setOwnerCopy(String(RATE_COPY));
    setOwnerScan(String(RATE_SCAN));
    setOwnerCustomerReam(String(RATE_CUSTOMER_REAM));
  }, [RATE_BW, RATE_COLOR, RATE_COPY, RATE_SCAN, RATE_CUSTOMER_REAM]);

  useEffect(() => {
    return () => {
      if (redirectJobRef.current) clearTimeout(redirectJobRef.current);
      if (redirectPayRef.current) clearTimeout(redirectPayRef.current);
    };
  }, []);

  useEffect(() => {
    if (mode === 'rates' && isOwner) {
      refreshPrintJobs();
    }
  }, [mode, isOwner, refreshPrintJobs]);

  const filteredJobs = useMemo(() => {
    if (isOwner) return printJobs;
    const identity = currentUser?.name || currentUser?.email;
    return printJobs.filter((job) => job.createdBy === identity);
  }, [printJobs, isOwner, currentUser]);

  const rateForType = useMemo(() => {
    switch (printType) {
      case 'color':
        return RATE_COLOR;
      case 'copy':
        return RATE_COPY;
      case 'scan':
        return RATE_SCAN;
      case 'customer-ream':
        return RATE_CUSTOMER_REAM;
      default:
        return RATE_BW;
    }
  }, [printType, RATE_BW, RATE_COLOR, RATE_COPY, RATE_SCAN, RATE_CUSTOMER_REAM]);

  const previewTotal = (Number(pages) || 0) * rateForType;

  const jobBusy = jobPhase === 'loading' || jobPhase === 'success';
  const payBusy = payPhase === 'loading' || payPhase === 'success';
  const ratesBusy = ratesPhase === 'loading' || ratesPhase === 'success';

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  const onPrintingStatKeyDown = (navKey) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go(navKey);
    }
  };

  const handleNewJob = async (e) => {
    e.preventDefault();
    if (jobPhase !== 'idle') return;
    if (redirectJobRef.current) {
      clearTimeout(redirectJobRef.current);
      redirectJobRef.current = null;
    }
    setFeedback(null);
    if (!pages || Number(pages) < 1) {
      setFeedback({ type: 'error', text: 'Please enter a valid number of pages.' });
      return;
    }
    setJobPhase('loading');
    const started = Date.now();
    try {
      const job = await addPrintJob({ pages, printType });
      await waitUntilMinElapsed(started);
      setFeedback({
        type: 'success',
        text: `Job saved. Customer owes TZS ${Number(job.totalAmount).toLocaleString()} (${printServiceLabel(printType)}).`,
      });
      setPages('');
      setPrintType('bw');
      setJobPhase('success');
      redirectJobRef.current = setTimeout(() => {
        redirectJobRef.current = null;
        setJobPhase('idle');
        setFeedback(null);
        go('printing-history');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch {
      setFeedback({ type: 'error', text: 'Could not save job.' });
      setJobPhase('idle');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (payPhase !== 'idle') return;
    if (redirectPayRef.current) {
      clearTimeout(redirectPayRef.current);
      redirectPayRef.current = null;
    }
    setFeedback(null);
    if (!paymentJob || !paymentAmount || Number(paymentAmount) <= 0) {
      setFeedback({ type: 'error', text: 'Select a job and enter a valid amount.' });
      return;
    }
    setPayPhase('loading');
    const started = Date.now();
    try {
      await recordPayment({ jobId: Number(paymentJob), amount: Number(paymentAmount) });
      await waitUntilMinElapsed(started);
      setFeedback({ type: 'success', text: 'Payment recorded successfully.' });
      setPaymentJob('');
      setPaymentAmount('');
      setPayPhase('success');
      redirectPayRef.current = setTimeout(() => {
        redirectPayRef.current = null;
        setPayPhase('idle');
        setFeedback(null);
        go('printing-history');
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch {
      setFeedback({ type: 'error', text: 'Could not record payment.' });
      setPayPhase('idle');
    }
  };

  const handleHistoryDownloadCsv = useCallback(() => {
    if (!isOwner) {
      setHistoryExportError('Only owner can export reports.');
      return;
    }
    if (filteredJobs.length === 0) return;
    setHistoryExportError('');
    const data = buildPrintJobExportRows(filteredJobs, printServiceLabel);
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadCsv(`printing-jobs-${isOwner ? 'all' : 'mine'}-${ts}.csv`, [PRINT_EXPORT_HEAD_CSV, ...data]);
  }, [filteredJobs, isOwner]);

  const handleHistoryDownloadPdf = useCallback(async () => {
    if (!isOwner) {
      setHistoryExportError('Only owner can export reports.');
      return;
    }
    if (filteredJobs.length === 0 || historyPdfInFlight.current) return;
    historyPdfInFlight.current = true;
    setHistoryPdfBusy(true);
    setHistoryExportError('');
    try {
      const data = buildPrintJobExportRows(filteredJobs, printServiceLabel);
      const title = isOwner
        ? `Print / copy / scan — all jobs (${data.length})`
        : `Print / copy / scan — my jobs (${data.length})`;
      await exportTableToPdf(title, PRINT_EXPORT_HEAD_PDF, data);
    } catch {
      setHistoryExportError('Could not create PDF. Check your connection and try again.');
    } finally {
      historyPdfInFlight.current = false;
      setHistoryPdfBusy(false);
    }
  }, [filteredJobs, isOwner]);

  const handleSaveRates = async (e) => {
    e.preventDefault();
    if (!isOwner || ratesBusy) return;
    setFeedback(null);
    setRatesPhase('loading');
    const started = Date.now();
    try {
      await savePrintLabSettings({
        rateBw: Number(ownerBw),
        rateColor: Number(ownerColor),
        rateCopy: Number(ownerCopy),
        rateScan: Number(ownerScan),
        rateCustomerReam: Number(ownerCustomerReam),
      });
      await waitUntilMinElapsed(started);
      setFeedback({ type: 'success', text: 'Rates updated. Staff will use these prices on new jobs.' });
      setRatesPhase('success');
      setTimeout(() => {
        setRatesPhase('idle');
        setFeedback(null);
      }, 2400);
    } catch (err) {
      setFeedback({ type: 'error', text: err?.message || 'Could not save rates.' });
      setRatesPhase('idle');
    }
  };

  return (
    <div className="printing-root page-bleed">
      <header className="printing-header">
        <h1 className="printing-title">Printing &amp; copy center</h1>
        <p className="printing-lede">
          Print, photocopy, and scan jobs. Totals use the owner&apos;s rate card — staff choose service and pages only.
        </p>
      </header>

      {typeof onNavigate === 'function' && (
        <div className="printing-subnav" role="tablist" aria-label="Printing sections">
          <button type="button" role="tab" aria-selected={mode === 'hub'} className={mode === 'hub' ? 'is-active' : ''} onClick={() => go('printing')}>
            Overview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'new-job'}
            className={mode === 'new-job' ? 'is-active' : ''}
            onClick={() => go('printing-new-job')}
          >
            New job
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'payments'}
            className={mode === 'payments' ? 'is-active' : ''}
            onClick={() => go('printing-payments')}
          >
            Receive payment
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'history'}
            className={mode === 'history' ? 'is-active' : ''}
            onClick={() => go('printing-history')}
          >
            History
          </button>
          {isOwner && (
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'rates'}
              className={mode === 'rates' ? 'is-active' : ''}
              onClick={() => go('printing-rates')}
            >
              Set rates (owner)
            </button>
          )}
        </div>
      )}

      <div className="printing-stats">
        <div
          className="printing-stat-card printing-stat-card--clickable"
          role="link"
          tabIndex={0}
          title="Open job history"
          onClick={() => go('printing-history')}
          onKeyDown={onPrintingStatKeyDown('printing-history')}
        >
          <span>Jobs</span>
          <strong>{stats.totalJobs}</strong>
        </div>
        <div
          className="printing-stat-card printing-stat-card--clickable"
          role="link"
          tabIndex={0}
          title="Open job history"
          onClick={() => go('printing-history')}
          onKeyDown={onPrintingStatKeyDown('printing-history')}
        >
          <span>Paid</span>
          <strong>TZS {stats.totalRevenue.toLocaleString()}</strong>
        </div>
        <div
          className="printing-stat-card printing-stat-card--clickable"
          role="link"
          tabIndex={0}
          title="Receive payment — pending balances"
          onClick={() => go('printing-payments')}
          onKeyDown={onPrintingStatKeyDown('printing-payments')}
        >
          <span>Pending</span>
          <strong>TZS {stats.pendingBalance.toLocaleString()}</strong>
        </div>
      </div>

      {mode === 'hub' && (
        <div className="printing-hub">
          <p className="printing-hub-intro">Choose what you want to do.</p>
          <div className="printing-hub-grid">
            <button type="button" className="printing-hub-card" onClick={() => go('printing-new-job')}>
              <h3>New job</h3>
              <p>Record pages for print, copy, or scan using the current rate card.</p>
            </button>
            <button type="button" className="printing-hub-card" onClick={() => go('printing-payments')}>
              <h3>Receive payment</h3>
              <p>Record a payment against an open balance.</p>
            </button>
            <button type="button" className="printing-hub-card" onClick={() => go('printing-history')}>
              <h3>Job history</h3>
              <p>View past jobs and balances{isOwner ? ' for all staff' : ' you created'}.</p>
            </button>
            {isOwner && (
              <button type="button" className="printing-hub-card printing-hub-card--accent" onClick={() => go('printing-rates')}>
                <h3>Set rates</h3>
                <p>Update price per page for each service (only you can change this).</p>
              </button>
            )}
          </div>
          <div className="printing-rate-strip" aria-label="Current rates per page">
            <span>B/W print TZS {RATE_BW.toLocaleString()}</span>
            <span>Color TZS {RATE_COLOR.toLocaleString()}</span>
            <span>Copy TZS {RATE_COPY.toLocaleString()}</span>
            <span>Scan TZS {RATE_SCAN.toLocaleString()}</span>
            <span>Customer own ream TZS {RATE_CUSTOMER_REAM.toLocaleString()}</span>
          </div>
        </div>
      )}

      {mode === 'new-job' && (
        <form className="printing-card" onSubmit={handleNewJob}>
          <h3>New job</h3>
          <p className="printing-staff-note">
            Staff: choose the service and number of pages only. <strong>Total is calculated on the server</strong> from the owner&apos;s rates — you cannot edit the price.
          </p>
          <label>
            Number of pages
            <input type="number" min="1" value={pages} onChange={(e) => setPages(e.target.value)} required disabled={jobBusy} />
          </label>
          <label>
            Service
            <select value={printType} onChange={(e) => setPrintType(e.target.value)} disabled={jobBusy}>
              <option value="bw">Black &amp; white print — TZS {RATE_BW.toLocaleString()}/page</option>
              <option value="color">Colored print — TZS {RATE_COLOR.toLocaleString()}/page</option>
              <option value="copy">Photocopy — TZS {RATE_COPY.toLocaleString()}/page</option>
              <option value="scan">Scan — TZS {RATE_SCAN.toLocaleString()}/page</option>
              <option value="customer-ream">Customer with own ream — TZS {RATE_CUSTOMER_REAM.toLocaleString()}/page</option>
            </select>
          </label>
          <div className="printing-preview">
            Estimated total: <strong>TZS {previewTotal.toLocaleString()}</strong>
            <span className="printing-preview-sub"> ({rateForType.toLocaleString()} × {Number(pages) || 0} pages)</span>
          </div>
          <button type="submit" className="printing-btn printing-btn--primary" disabled={jobBusy}>
            {jobPhase === 'loading' ? (
              <>
                <span className="submit-spinner" aria-hidden />
                Saving…
              </>
            ) : jobPhase === 'success' ? (
              'Redirecting…'
            ) : (
              'Save job'
            )}
          </button>
        </form>
      )}

      {mode === 'payments' && (
        <form className="printing-card" onSubmit={handlePayment}>
          <h3>Receive payment</h3>
          <label>
            Select job
            <select value={paymentJob} onChange={(e) => setPaymentJob(e.target.value)} required disabled={payBusy}>
              <option value="">Select pending job</option>
              {filteredJobs
                .filter((job) => job.balance > 0)
                .map((job) => (
                  <option key={job.id} value={job.id}>
                    #{job.id} — {printServiceLabel(job.printType)} — balance TZS {Number(job.balance).toLocaleString()}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Amount paid
            <input
              type="number"
              min="1"
              step="0.01"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              required
              disabled={payBusy}
            />
          </label>
          <button type="submit" className="printing-btn printing-btn--primary" disabled={payBusy}>
            {payPhase === 'loading' ? (
              <>
                <span className="submit-spinner" aria-hidden />
                Saving…
              </>
            ) : payPhase === 'success' ? (
              'Redirecting…'
            ) : (
              'Record payment'
            )}
          </button>
        </form>
      )}

      {mode === 'history' && (
        <div className="printing-card printing-card--history">
          <div className="printing-history-head">
            <h3 className="printing-history-title">{isOwner ? 'All print / copy / scan jobs' : 'My jobs'}</h3>
            <div className="printing-history-actions">
              <button
                type="button"
                className="printing-btn printing-btn--secondary"
                onClick={() => refreshPrintJobs()}
                title="Reload jobs from server"
              >
                Refresh
              </button>
              {isOwner ? (
                <>
                  <button
                    type="button"
                    className="printing-btn printing-btn--secondary"
                    onClick={handleHistoryDownloadCsv}
                    disabled={filteredJobs.length === 0 || historyPdfBusy}
                    title="Download as spreadsheet (CSV)"
                  >
                    Download CSV
                  </button>
                  <button
                    type="button"
                    className="printing-btn printing-btn--pdf"
                    onClick={() => void handleHistoryDownloadPdf()}
                    disabled={filteredJobs.length === 0 || historyPdfBusy}
                    aria-busy={historyPdfBusy}
                    title="Download PDF report"
                  >
                    {historyPdfBusy ? 'PDF…' : 'Download PDF'}
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {historyExportError && (
            <p className="printing-export-error" role="alert">
              {historyExportError}
            </p>
          )}
          {filteredJobs.length === 0 ? (
            <div className="printing-empty">
              <p className="printing-empty-title">No jobs yet</p>
              <p className="printing-empty-text">Create a new job to see it listed here with balance and status.</p>
            </div>
          ) : (
            <div className="printing-table-wrap">
              <table className="printing-table">
                <thead>
                  <tr>
                    <th className="printing-th printing-th--id">ID</th>
                    <th className="printing-th printing-th--num">Pages</th>
                    <th>Service</th>
                    <th className="printing-th printing-th--num">Rate</th>
                    <th className="printing-th printing-th--num">Total</th>
                    <th className="printing-th printing-th--num">Paid</th>
                    <th className="printing-th printing-th--num">Balance</th>
                    <th>Status</th>
                    <th>By</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job) => (
                    <tr key={job.id} className="printing-tr">
                      <td className="printing-td printing-td--id">#{job.id}</td>
                      <td className="printing-td printing-td--num">{job.pages}</td>
                      <td className="printing-td printing-td--service">{printServiceLabel(job.printType)}</td>
                      <td className="printing-td printing-td--num">{Number(job.rate).toLocaleString()}</td>
                      <td className="printing-td printing-td--num printing-td--strong">
                        {Number(job.totalAmount).toLocaleString()}
                      </td>
                      <td className="printing-td printing-td--num">{Number(job.paidAmount).toLocaleString()}</td>
                      <td className="printing-td printing-td--num">{Number(job.balance).toLocaleString()}</td>
                      <td>
                        <span
                          className={`printing-badge printing-badge--${statusVariant(job.status)}`}
                          title={String(job.status || '')}
                        >
                          {String(job.status || '—')}
                        </span>
                      </td>
                      <td className="printing-td printing-td--by">{job.createdBy}</td>
                      <td className="printing-td printing-td--date">{formatJobDate(job.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {mode === 'rates' && (
        <>
          {!isOwner ? (
            <div className="printing-card">
              <p>Only the business owner can change printing and copy rates.</p>
            </div>
          ) : (
            <form className="printing-card printing-card--rates" onSubmit={handleSaveRates}>
              <h3>Set rates (owner)</h3>
              <p className="printing-staff-note">These amounts apply to every new job. Staff only enter pages and service type.</p>
              <div className="printing-rates-grid">
                <label>
                  B/W print (TZS / page)
                  <input type="number" min="0" step="0.01" value={ownerBw} onChange={(e) => setOwnerBw(e.target.value)} required disabled={ratesBusy} />
                </label>
                <label>
                  Color print (TZS / page)
                  <input type="number" min="0" step="0.01" value={ownerColor} onChange={(e) => setOwnerColor(e.target.value)} required disabled={ratesBusy} />
                </label>
                <label>
                  Photocopy (TZS / page)
                  <input type="number" min="0" step="0.01" value={ownerCopy} onChange={(e) => setOwnerCopy(e.target.value)} required disabled={ratesBusy} />
                </label>
                <label>
                  Scan (TZS / page)
                  <input type="number" min="0" step="0.01" value={ownerScan} onChange={(e) => setOwnerScan(e.target.value)} required disabled={ratesBusy} />
                </label>
                <label>
                  Customer with own ream (TZS / page)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={ownerCustomerReam}
                    onChange={(e) => setOwnerCustomerReam(e.target.value)}
                    required
                    disabled={ratesBusy}
                  />
                </label>
              </div>
              <button type="submit" className="printing-btn printing-btn--primary" disabled={ratesBusy}>
                {ratesPhase === 'loading' ? (
                  <>
                    <span className="submit-spinner" aria-hidden />
                    Saving…
                  </>
                ) : ratesPhase === 'success' ? (
                  'Saved'
                ) : (
                  'Save rates'
                )}
              </button>
            </form>
          )}
        </>
      )}

      {feedback && (
        <div
          className={`printing-message${feedback.type === 'success' ? ' printing-message--success' : ' printing-message--error'}`}
        >
          {feedback.text}
        </div>
      )}
    </div>
  );
};

export default PrintingPage;
